import React, { useRef } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import {
  FarmClass,
  FarmRegionalCouncil,
  FarmRegion,
  LivestockClass,
  LivestockBalanceItem,
  LivestockClassType,
  LivestockTypeName,
  LivestockMovementItem,
  GhgConstants,
  GhgCalculation,
  GhgCalculation_POST,
  GhgCalculationDraft_POST,
  GhgCalculationResult,
  Farmer,
} from 'api/models'
import sortBy from 'lodash/sortBy'
import { useAsync } from 'hooks/use-async'
import { getApi, postApi, putApi } from 'api/fetch'
import {
  FarmDetailState,
  ghgFormLocalStorageKey,
  GrazingOffMovementItemFrontend,
  GrazingOnMovementItemFrontend,
  FullState,
  LocalStorageState,
} from './ghg-calculator.state'
import {
  GhgNavStep,
  GHG_STEPS,
  findPreviousStep,
  findNextStep,
  GHG_STEPS_SEQUENCE,
} from './ghg-calculator.nav'
import { UseFormMethods } from 'react-hook-form'
import { calculateLoss, useOnHandOpenCloseCalculation } from './use-ghg-calculation'
import { getSeasonRange, SEASON_DEFAULT_END_MONTH } from 'utils/season'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RegisterFormFn = (formBag: UseFormMethods<any> | null) => void

export function defaultFarmDetailState(): FarmDetailState {
  return {
    name: '',
    gst_number: '',
    regional_council: null,
    region: null,
    classification: null,
    area_total: null,
    area_effective: null,
    analysis_date: format(new Date(), 'yyyy-MM-dd'),
    season_year: null,
    end_month: SEASON_DEFAULT_END_MONTH,
    weight_non_urea_nitrogen: null,
    weight_urea_nitrogen: null,
    weight_urea_urease_inhibitor: null,
    weight_limestone: null,
    weight_dolomite: null,
    area_exotic_forest_existing: null,
    area_exotic_forest_harvested: null,
    area_indigenous_forest_regenerating_existing: null,
    area_indigenous_forest_established_existing: null,
    area_indigenous_forest_established_harvested: null,
    area_shrubland_lt30_existing: null,
    area_shrubland_gt30_existing: null,
    area_shrubland_gt30_harvested: null,
  }
}

export function defaultLivestockBalancesState(
  livestockClasses: LivestockClass[]
): LivestockBalanceItem[] {
  return livestockClasses
    .filter((livestockClass) => !livestockClass.is_births_class)
    .map(({ id }) => {
      return {
        classification: id,
        owned_on_at_open: null,
        owned_on_at_close: null,
        owned_off_at_open: null,
        owned_off_at_close: null,
        grazed_on_at_open: null,
        grazed_on_at_close: null,
      }
    })
}

export function defaultLivestockMovementsState(
  livestockClasses: LivestockClass[]
): LivestockMovementItem[] {
  return livestockClasses.map(({ id }) => {
    return {
      classification: id,
      births: null,
      purchases: null,
      sales_store: null,
      sales_prime: null,
      used_on_farm: null,
    }
  })
}

export function makeDefaultGrazingOnItem(): GrazingOnMovementItemFrontend {
  return {
    classification: null as any, // eslint-disable-line
    tally_arrive: null,
    tally_leave: null,
    arrived_date: null,
    left_date: null,
    on_hand_at_open: false,
    on_hand_at_close: false,
  }
}

export function makeDefaultGrazingOffItem(): GrazingOffMovementItemFrontend {
  return {
    classification: null as any, // eslint-disable-line
    tally_depart: null,
    tally_return: null,
    departed_date: null,
    returned_date: null,
    on_hand_at_open: false,
    on_hand_at_close: false,
  }
}

// TODO: maybe add a "position" flag to the back end?
const stockTypeOrder: LivestockTypeName[] = ['SHEEP', 'BEEF', 'DAIRY', 'DEER']

async function fetchLivestockClassTypesFn(): Promise<LivestockClassType[]> {
  return sortBy(await getApi('/api/ghg/v1/livestockclasstype'), ({ name }) =>
    stockTypeOrder.findIndex((ordering) => ordering === name)
  )
}

async function fetchLivestockClassesFn(): Promise<LivestockClass[]> {
  return sortBy(await getApi('/api/ghg/v1/livestockclass'), (c) => c.position)
}

function fetchFarmRegionalCouncilsFn(): Promise<FarmRegionalCouncil[]> {
  return getApi('/api/ghg/v1/regionalcouncil')
}

function fetchFarmRegionsFn(): Promise<FarmRegion[]> {
  return getApi('/api/ghg/v1/farmregion')
}

function fetchFarmClassesFn(): Promise<FarmClass[]> {
  return getApi('/api/ghg/v1/farmclass')
}

function fetchGhgConstantsFn(): Promise<GhgConstants> {
  return getApi('/api/ghg/v1/constants')
}

function fetchCalculation(id: string): Promise<GhgCalculation> {
  return getApi(`/api/ghg/v1/calculation/${id}` as '/api/ghg/v1/calculation/:id')
}

function fetchPreviewFn(payload: GhgCalculation_POST): Promise<GhgCalculationResult> {
  return postApi('/api/ghg/v1/calculation/preview', payload)
}

function saveCalculationFn(
  id: null | string,
  payload: GhgCalculation_POST
): Promise<GhgCalculation> {
  if (id) {
    return putApi(`/api/ghg/v1/calculation/${id}` as '/api/ghg/v1/calculation/:id', payload)
  }
  return postApi('/api/ghg/v1/calculation', payload)
}

export const NEGATIVE_LOSS_ERROR_MSG =
  'Negative losses have occurred, this is likely due to livestock not being aged up at season close correctly,' +
  ' or livestock in one age class at open have been sold or traded in a different class.' +
  ' Please refer to Step 4 and Step 6 in the user guide.'

interface Props {
  user: Farmer
  calculationId?: string
}

export function useGhgCalculatorForm(props: Props) {
  const {
    user: { id: userId },
    calculationId,
  } = props
  const localStorageKey = ghgFormLocalStorageKey(userId)

  const [isInitialized, setInitialized] = useState(false)
  const [calculationFetchErrorMessage, setCalculationFetchErrorMessage] = useState('')

  const [ghgConstants, fetchGhgConstants] = useAsync(fetchGhgConstantsFn)
  const [livestockClasses, fetchLivestockClasses] = useAsync(fetchLivestockClassesFn)
  const [livestockClassTypes, fetchLivestockClassTypes] = useAsync(fetchLivestockClassTypesFn)
  const [farmRegionalCouncils, fetchFarmRegionalCouncils] = useAsync(fetchFarmRegionalCouncilsFn)
  const [farmRegions, fetchFarmRegions] = useAsync(fetchFarmRegionsFn)
  const [farmClasses, fetchFarmClasses] = useAsync(fetchFarmClassesFn)
  const [previewResultResponse, fetchResultPreview] = useAsync(fetchPreviewFn)
  const [saveCalculationResponse, callSaveCalculation] = useAsync(saveCalculationFn)

  const [currentStep, setCurrentStep] = useState<GhgNavStep>(
    calculationId ? GHG_STEPS.RESULT : GHG_STEPS.FARM_DETAIL
  )

  const currentFormBag = useRef<UseFormMethods | null>(null)
  const registerForm: RegisterFormFn = useCallback((formBag) => {
    currentFormBag.current = formBag
  }, [])

  const [farmDetailDefaultValues] = useState<FarmDetailState>(defaultFarmDetailState())
  const [farmDetail, setFarmDetail] = useState<FarmDetailState>(farmDetailDefaultValues)
  const [livestockBalances, setLivestockBalances] = useState<LivestockBalanceItem[]>()
  const [livestockMovements, setLivestockMovements] = useState<LivestockMovementItem[]>()
  const [grazingOnMovements, setGrazingOnMovements] = useState<GrazingOnMovementItemFrontend[]>([
    makeDefaultGrazingOnItem(),
  ])
  const [grazingOffMovements, setGrazingOffMovements] = useState<GrazingOffMovementItemFrontend[]>([
    makeDefaultGrazingOffItem(),
  ])

  const finalState: FullState = useMemo(() => {
    return {
      currentStep,
      farmDetail,
      livestockBalances,
      livestockMovements,
      grazingOnMovements,
      grazingOffMovements,
    }
  }, [
    currentStep,
    farmDetail,
    livestockBalances,
    livestockMovements,
    grazingOnMovements,
    grazingOffMovements,
  ])

  // fetch all the options used for the form
  useEffect(() => {
    fetchGhgConstants()
    fetchLivestockClassTypes()
    fetchLivestockClasses()
    fetchFarmRegions()
    fetchFarmRegionalCouncils()
    fetchFarmClasses()
  }, [
    fetchFarmRegions,
    fetchFarmRegionalCouncils,
    fetchFarmClasses,
    fetchLivestockClasses,
    fetchLivestockClassTypes,
    fetchGhgConstants,
  ])

  const isLoading = useMemo(() => {
    return (
      ghgConstants.loading ||
      farmRegions.loading ||
      farmRegionalCouncils.loading ||
      farmClasses.loading ||
      livestockClassTypes.loading ||
      livestockClasses.loading
    )
  }, [
    ghgConstants,
    livestockClassTypes,
    livestockClasses,
    farmRegions,
    farmRegionalCouncils,
    farmClasses,
  ])

  useEffect(() => {
    if (calculationId) {
      fetchCalculation(calculationId)
        .then((calc) => {
          setLivestockBalances(calc.livestock_balance_items)
          setLivestockMovements(calc.livestock_movement_items)
          setGrazingOnMovements(
            calc.grazing_on_movement_items.map((iter) => ({
              ...iter,
              on_hand_at_open: !iter.arrived_date,
              on_hand_at_close: !iter.left_date,
            }))
          )
          setGrazingOffMovements(
            calc.grazing_off_movement_items.map((iter) => ({
              ...iter,
              on_hand_at_open: !iter.departed_date,
              on_hand_at_close: !iter.returned_date,
            }))
          )
          setFarmDetail({
            ...calc.farm,
            season_year: calc.season_year,
          })
        })
        .catch(() => {
          setCalculationFetchErrorMessage(
            `Calculation with id = ${calculationId} can not be found.`
          )
        })
        .finally(() => {
          setInitialized(true)
        })
    } else {
      // resurrect the form from local storage
      const storedFormValues = localStorage.getItem(localStorageKey)

      try {
        const formValuesJson = JSON.parse(storedFormValues || '') as LocalStorageState
        setFarmDetail(formValuesJson.farmDetail)
        setLivestockBalances(formValuesJson.livestockBalances)
        setLivestockMovements(formValuesJson.livestockMovements)
        setCurrentStep(formValuesJson.currentStep)
        setGrazingOnMovements(formValuesJson.grazingOnMovements)
        setGrazingOffMovements(formValuesJson.grazingOffMovements)
      } catch (e) {
        // do nothing
      } finally {
        setInitialized(true)
      }
    }
  }, [calculationId, localStorageKey])

  useEffect(() => {
    // when user creating a new calculation
    // persist the form state to local storage whenever any state changed
    if (isInitialized && !calculationId) {
      const localStorageState: LocalStorageState = {
        ...finalState,
        draftModifiedAt: new Date().toISOString(),
      }
      localStorage.setItem(localStorageKey, JSON.stringify(localStorageState))
    }
  }, [isInitialized, finalState, calculationId, localStorageKey])

  // create the default livestockBalances when livestocClasses is available
  // & no data is loaded from localStorage
  useEffect(() => {
    if (isInitialized && livestockClasses.data) {
      setLivestockBalances((items) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        items?.length ? items : defaultLivestockBalancesState(livestockClasses.data!)
      )
      setLivestockMovements((items) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        items?.length ? items : defaultLivestockMovementsState(livestockClasses.data!)
      )
    }
  }, [isInitialized, livestockClasses.data])

  const [previousStep, nextStep] = useMemo(
    () => [findPreviousStep(currentStep), findNextStep(currentStep)],
    [currentStep]
  )

  const saveDraft = useCallback(async () => {
    if (!calculationId && isInitialized && !isLoading && currentFormBag.current) {
      const { name, gst_number, regional_council } = currentFormBag.current.getValues()

      if (gst_number && regional_council) {
        const payload: GhgCalculationDraft_POST = {
          name: name,
          gst_number: gst_number,
          regional_council: regional_council,
        }

        postApi('/api/ghg/v1/calculation/draft', payload)
      }
    }
  }, [calculationId, isInitialized, isLoading, currentFormBag])

  const goToStep = useCallback(
    (stepId: string | null) => {
      const step = stepId
        ? GHG_STEPS_SEQUENCE.find((iter) => iter.id === stepId)
        : GHG_STEPS_SEQUENCE[0]
      if (step) {
        if (currentStep && currentStep.id === 'farm-detail') {
          // Save draft if leaving 'Your Farm' tab
          saveDraft()
        }
        setCurrentStep(step)
      }
    },
    [currentStep, setCurrentStep, saveDraft]
  )

  const goToNextStep = useCallback(() => {
    if (nextStep) {
      goToStep(nextStep.id)
    }
  }, [nextStep, goToStep])

  const goToPreviousStep = useCallback(() => {
    if (previousStep) {
      goToStep(previousStep.id)
    }
  }, [previousStep, goToStep])

  const reset = useCallback(() => {
    localStorage.removeItem(localStorageKey)
    window.location.reload()
  }, [localStorageKey])

  const submissionPayload = useMemo(() => {
    if (
      !farmDetail ||
      !livestockBalances ||
      !livestockMovements ||
      !grazingOnMovements ||
      !grazingOffMovements
    ) {
      return null
    }

    const { season_year, ...farm } = farmDetail

    if (!season_year) {
      return null
    }

    return {
      farm,
      season_year,
      livestock_balance_items: livestockBalances,
      livestock_movement_items: livestockMovements,
      grazing_on_movement_items: grazingOnMovements
        .filter((iter) => Boolean(iter.classification))
        .map((iter) => ({
          ...iter,
          arrived_date: iter.arrived_date || null,
          left_date: iter.left_date || null,
        })),
      grazing_off_movement_items: grazingOffMovements
        .filter((iter) => Boolean(iter.classification))
        .map((iter) => ({
          ...iter,
          departed_date: iter.departed_date || null,
          returned_date: iter.returned_date || null,
        })),
    }
  }, [farmDetail, livestockBalances, livestockMovements, grazingOnMovements, grazingOffMovements])

  const saveCalculation = useCallback(async () => {
    if (!submissionPayload) {
      return
    }

    callSaveCalculation(calculationId || null, submissionPayload)
      .then(() => {
        if (!calculationId) {
          localStorage.removeItem(localStorageKey)
        }
        window.location.assign('/')
      })
      .catch(() => {
        // do nothing
      })
  }, [calculationId, submissionPayload, callSaveCalculation, localStorageKey])

  const previewResult = useCallback(async () => {
    if (!submissionPayload) {
      return
    }
    fetchResultPreview(submissionPayload).catch(() => {
      // do nothing
    })
  }, [submissionPayload, fetchResultPreview])

  const { onHandAtOpen, onHandAtClose } = useOnHandOpenCloseCalculation(livestockBalances || [])
  const negativeLossErrors = useMemo(() => {
    if (
      !farmDetail.season_year ||
      !ghgConstants.data ||
      !livestockMovements ||
      !grazingOnMovements ||
      !grazingOffMovements ||
      !livestockClasses.data ||
      !livestockClassTypes.data
    ) {
      return null
    }

    const [seasonStart, seasonEnd] = getSeasonRange(farmDetail.season_year, farmDetail.end_month)

    const losses = calculateLoss(
      livestockMovements,
      onHandAtOpen,
      onHandAtClose,
      grazingOnMovements,
      grazingOffMovements,
      livestockClasses.data,
      livestockClassTypes.data,
      seasonStart,
      seasonEnd
    )

    const negativeLossErrors = Object.entries(losses).reduce((acc, [key, loss]) => {
      if (
        (loss.adultInput > 0 && loss.adultHeads < 0) ||
        (loss.babyInput > 0 && loss.babyHeads < 0)
      ) {
        acc[key as LivestockTypeName] = NEGATIVE_LOSS_ERROR_MSG
      }
      return acc
    }, {} as { [key in LivestockTypeName]?: string })

    if (Object.keys(negativeLossErrors).length > 0) {
      return { loss: negativeLossErrors }
    }
  }, [
    livestockMovements,
    onHandAtOpen,
    onHandAtClose,
    grazingOnMovements,
    grazingOffMovements,
    livestockClasses.data,
    livestockClassTypes.data,
    farmDetail.season_year,
    farmDetail.end_month,
    ghgConstants.data,
  ])

  return {
    isInitialized,
    calculationFetchErrorMessage,
    isLoading,
    farmDetail,
    setFarmDetail,
    livestockBalances,
    setLivestockBalances,
    livestockMovements,
    setLivestockMovements,
    ghgConstants: ghgConstants.data,
    grazingOnMovements,
    setGrazingOnMovements,
    grazingOffMovements,
    setGrazingOffMovements,
    livestockClassTypes: livestockClassTypes.data,
    livestockClasses: livestockClasses.data,
    farmRegions: farmRegions.data || [],
    farmRegionalCouncils: farmRegionalCouncils.data || [],
    farmClasses: farmClasses.data || [],
    currentFormBag,
    registerForm,
    currentStep,
    nextStep,
    previousStep,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    reset,
    saveCalculation,
    saveCalculationResponse,
    previewResult,
    previewResultResponse,
    negativeLossErrors,
  }
}

export type GhgCalculatorBag = ReturnType<typeof useGhgCalculatorForm>
export const GhgCalculatorFormContext = React.createContext<GhgCalculatorBag>({
  isInitialized: false,
  calculationFetchErrorMessage: '',
  isLoading: false,
  farmDetail: defaultFarmDetailState(),
  setFarmDetail: () => {},
  livestockBalances: [],
  setLivestockBalances: () => {},
  livestockMovements: [],
  setLivestockMovements: () => {},
  ghgConstants: null,
  currentFormBag: { current: null },
  registerForm: () => {},
  grazingOnMovements: [],
  setGrazingOnMovements: () => {},
  grazingOffMovements: [],
  setGrazingOffMovements: () => {},
  livestockClassTypes: null,
  livestockClasses: null,
  farmRegions: [],
  farmRegionalCouncils: [],
  farmClasses: [],
  currentStep: GHG_STEPS.FARM_DETAIL,
  nextStep: null,
  previousStep: null,
  goToNextStep: () => {},
  goToPreviousStep: () => {},
  goToStep: () => {},
  reset: () => {},
  saveCalculation: () => Promise.resolve(),
  saveCalculationResponse: { loading: false, data: null, lastFetched: null },
  previewResult: () => Promise.resolve(),
  previewResultResponse: { loading: false, data: null, lastFetched: null },
  negativeLossErrors: null,
})

export const useGhgCalculatorFormContext = () => {
  return useContext(GhgCalculatorFormContext)
}
